import { Component } from 'react';
import styles from './DocumentHistory.scss';
import moment from 'moment';
import lodash from 'lodash';

import DateTimePicker from 'common/components/DateTime/DateTimePicker';
import DateTime from 'common/components/DateTime/DateTime';
import Part11Indicator from 'common/components/Part11Indicator/Part11Indicator';
import { DEFAULT_DATETIME_INPUT_FORMAT } from 'common/util/dateTime';

const actionDisplayNames = {
    downloadSource: 'Downloaded the Source Document',
    downloadPdf: 'Downloaded the PDF',
    downloadPdfSignatures: 'Download PDF with Signatures',
    downloadPdfHistory: 'Download PDF with History',
    index: 'Modified Document Attributes',
    initialIndex: 'Assigned Document Type and Added Document Attributes',
    reindex: 'Changed the Document Type',
    approveAndSign: 'Signed this Document',
    sign: 'Signed this Document',
    upload: 'Uploaded this Document',
    viewDocument: 'Viewed the Document',
    readAndAcknowledge: 'Viewed the Document',
    certifyAndSign: 'Signed this Document',
    reviewAndSign: 'Signed this Document',
    trainAndSign: 'Signed this Document',
    restoreInitialIndex: 'Restored Original Indexing User/Time',
};

const Part11IndicatorActions = [
    'initialIndex',
    'approveAndSign',
    'certifyAndSign',
    'reviewAndSign',
    'trainAndSign',
    'sign'
];

export class DocumentHistory extends Component {
    constructor(props) {
        super(props);
        this.state = {
            dateRange: {
                start: moment().startOf('day').toDate(),
                end: moment().endOf('day').toDate(),
            },
            dateRangeError: null,
        };
    }

    _setDateRangeError(dateRangeError) {
        this.setState({ dateRangeError });
    }

    _validateDateRange({ start, end }) {
        const isStartValid = moment(start).isValid();
        const isEndValid = moment(end).isValid();
        const isStartBeforeEnd = moment(start).isBefore(moment(end));

        return isStartValid && isStartBeforeEnd && isEndValid;
    }

    reloadDocumentHistory() {
        const { loadDocumentHistory } = this.props;
        const { dateRange } = this.state;

        if (this._validateDateRange(dateRange)) {
            this._setDateRangeError(null);
            loadDocumentHistory(dateRange);
        } else {
            this._setDateRangeError('Please provide a valid date range');
        }
    }

    onStartDateChange(date) {
        const { dateRange } = this.state;
        this.setState(
            {
                dateRange: {
                    start: moment(date).startOf('day').toDate(),
                    end: dateRange.end,
                },
            },
            this.reloadDocumentHistory.bind(this)
        );
    }

    onEndDateChange(date) {
        const { dateRange } = this.state;

        this.setState(
            {
                dateRange: {
                    start: dateRange.start,
                    end: moment(date).endOf('day').toDate(),
                },
            },
            this.reloadDocumentHistory.bind(this)
        );
    }

    render() {
        const { history } = this.props;
        const {
            dateRange: { start: startDate, end: endDate },
            dateRangeError,
        } = this.state;

        return (
            <section className={styles.historyContainer} data-cy="DocumentHistory">
                <header className={styles.header}>Document History</header>
                <div className={styles.historyArea}>
                    <div className={styles.rangePicker}>
                        <p>Select a date range to view document history</p>
                        <DateRangeError errorMessage={dateRangeError} />

                        <div className={styles.datePicker}>
                            <span>Start</span>
                            <DateTimePicker
                                defaultValue={startDate}
                                onSelect={this.onStartDateChange.bind(this)}
                                onChange={() => { }}
                                time={false}
                            />
                        </div>
                        <div className={styles.datePicker}>
                            <span>End</span>
                            <DateTimePicker
                                defaultValue={endDate}
                                onSelect={this.onEndDateChange.bind(this)}
                                onChange={() => { }}
                                time={false}
                            />
                        </div>
                    </div>
                    <HistoryElements history={dateRangeError ? [] : history} />
                </div>
            </section>
        );
    }
}

const DateRangeError = ({ errorMessage }) =>
    errorMessage && (
        <div className={styles.row} data-cy="DocumentHistory-DateRangeError">
            <span className={styles.historyRecordsError}>{errorMessage}</span>
        </div>
    );

function actionDataItems(actionDataItems) {
    if (!lodash.isEmpty(actionDataItems)) {
        return lodash.map(actionDataItems, (actionDataItem, index) => {
            return (
                <div className={styles.historyData} key={index} data-cy={'DocumentHistory-' + index}>
                    <span className={styles.historyDataItem} data-cy={'type-DocumentHistory-' + index}>
                        {' '}
                        {actionDataItem.type}{' '}
                    </span>
                    <span className={styles.historyData} data-cy={'value-DocumentHistory-' + index}>
                        {' '}
                        {actionDataItem.value}{' '}
                    </span>
                </div>
            );
        });
    }
}

function sortReverseChronologicalOrder(dates, sortProperty, format = null) {
    const datesClone = dates.slice();
    return datesClone.sort((a, b) => {
        const aDate = moment(a[sortProperty], format);
        const bDate = moment(b[sortProperty], format);
        return bDate.diff(aDate);
    });
}

function HistoryElements({ history }) {
    const sortedHistory = sortReverseChronologicalOrder(history, 'date');

    const historyElements = sortedHistory.map((record, i) => {
        const actionsByDate = sortReverseChronologicalOrder(record.actions, 'timestamp', DEFAULT_DATETIME_INPUT_FORMAT);
        const date = lodash.get(lodash.head(actionsByDate), 'timestamp') || record.date;

        const actions = actionsByDate.map((action, i) => {
            const actionName = lodash.camelCase(action.action);
            const actionData = action.data;
            return (
                <div key={i} className={styles.row} data-cy={`${i}-DocumentHistory`}>
                    <aside className={styles.time}>
                        <DateTime
                            value={action.timestamp}
                            inputFormat={DEFAULT_DATETIME_INPUT_FORMAT}
                            time={true}
                            format={'hh:mm a'}
                        />
                    </aside>
                    <section className={styles.details}>
                        <strong className={styles.actionTaker} data-cy={`${i}-DocumentHistory-name`}>
                            {action.user.name}
                        </strong>
                        <div className={styles.historyData}>
                            <span className={styles.action}>
                                {actionName !== 'postDocumentToThirdParties'
                                    ? actionDisplayNames[actionName]
                                    : action.message}
                                {lodash.includes(Part11IndicatorActions, actionName) && (
                                    <Part11Indicator className={styles.part11Icon} />
                                )}
                            </span>
                            <span className={styles.actionData}>{actionDataItems(actionData)}</span>
                        </div>
                    </section>
                </div>
            );
        });

        return (
            <div key={i} className={styles.record} data-cy={'DocumentHistory-div'}>
                <header className={styles.header}>
                    <DateTime value={date} inputFormat={DEFAULT_DATETIME_INPUT_FORMAT} applyTimezoneOffset={true} />
                </header>
                <section className={styles.content} data-cy={'DocumentHistory-actions'}>
                    {actions}
                </section>
            </div>
        );
    });

    if (lodash.isEmpty(history)) {
        return (
            <div className={styles.historyRecords} data-cy="DocumentHistory-isEmpty">
                <p>There were no actions taken on this document for the selected date range</p>
            </div>
        );
    }

    return (
        <div className={styles.historyRecords} data-cy="DocumentHistory-historyElements">
            {historyElements}
        </div>
    );
}
